import graphql from "babel-plugin-relay/macro";
import { Provider, useAtomValue, useSetAtom } from "jotai";
import { useFragment } from "react-relay";

import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { usePackageDisplayName } from "@whoosh/features/src/Packages";

import { currentPageAtom, goToPageAtom } from "../atoms";
import { StorePackageAuth } from "./StorePackageAuth";
import { StorePackageDetail } from "./StorePackageDetail";
import { StorePackageDetailFooter } from "./StorePackageDetailFooter";
import { StorePackagePurchase } from "./StorePackagePurchase";
import { StorePackagePurchaseFooter } from "./StorePackagePurchaseFooter";
import { StorePackageSuccess } from "./StorePackageSuccess";
import { StorePackageModal_package$key } from "./__generated__/StorePackageModal_package.graphql";
import { StorePackageModal_session$key } from "./__generated__/StorePackageModal_session.graphql";

type Props = {
  packageRef?: StorePackageModal_package$key | null;
  sessionRef?: StorePackageModal_session$key | null;
  onClose: () => void;
  isOpen: boolean;
  onAuthComplete?: () => void;
};

export const StorePackageModal = (props: Props) => {
  return (
    <Provider>
      <StorePackageModalInner {...props} />
    </Provider>
  );
};

export const StorePackageModalInner = ({
  packageRef,
  sessionRef,
  isOpen,
  onClose,
  onAuthComplete,
}: Props) => {
  const session = useFragment(
    StorePackageModalSessionFragmentString,
    sessionRef
  );
  const pkg = useFragment(StorePackageModalFragmentString, packageRef);
  const displayName = usePackageDisplayName(pkg);
  const currentPage = useAtomValue(currentPageAtom);
  const goToPage = useSetAtom(goToPageAtom);

  const onSignUpPress = () => {
    goToPage({ page: "AUTH" });
  };

  const onSuccessfulDraftAssignment = (
    memberPackageId: string,
    orderId: string
  ) => {
    goToPage({
      page: "PURCHASE",
      memberPackageId,
      orderId,
    });
  };

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      upperTitle={
        currentPage === "AUTH" || currentPage === "SUCCESS"
          ? undefined
          : displayName
      }
      hideCloseButton={currentPage === "AUTH" || currentPage === "SUCCESS"}
      hideFooterPadding
      disableContentScroll={currentPage === "SUCCESS" || currentPage === "AUTH"}
      allowShrink={currentPage === "SUCCESS"}
      content={
        {
          DETAIL: <StorePackageDetail packageRef={pkg} />,
          PURCHASE: <StorePackagePurchase />,
          AUTH: (
            <StorePackageAuth
              sessionRef={session}
              onAuthComplete={onAuthComplete}
            />
          ),
          SUCCESS: <StorePackageSuccess packageRef={pkg} />,
        }[currentPage]
      }
      footerContent={
        {
          DETAIL: (
            <StorePackageDetailFooter
              sessionRef={session}
              packageRef={pkg}
              onSignUpPress={onSignUpPress}
              onSuccessfulDraftAssignment={onSuccessfulDraftAssignment}
            />
          ),
          PURCHASE: <StorePackagePurchaseFooter />,
          AUTH: null,
          SUCCESS: null,
        }[currentPage]
      }
    />
  );
};

const StorePackageModalFragmentString = graphql`
  fragment StorePackageModal_package on Package {
    ...StorePackageDetail_package
    ...usePackageDisplayName_package
    ...StorePackageDetailFooter_package
    ...StorePackageSuccess_package
  }
`;

const StorePackageModalSessionFragmentString = graphql`
  fragment StorePackageModal_session on Session {
    ...StorePackageDetailFooter_session
    ...StorePackageAuth_session
  }
`;
